import React, { useEffect, useState } from "react";
import cn from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Card from "../../../components/Card";
import Modal from "../../../components/Modal";
import { fetchRecentGames } from "../../../app/stats/stats.slice";
import styles from "./RecentGames.module.sass";

const CARD_ITEMS = 8;
const MAX_TAKE = 30; // límite del endpoint

const gradients = [
  ["#FFBC99", "#FF8A65"],
  ["#CABDFF", "#8E59FF"],
  ["#B1E5FC", "#2A85FF"],
  ["#B5E4CA", "#83BF6E"],
  ["#FFD88D", "#FFB020"],
  ["#FFA5D8", "#FF6AC2"],
];
const gradFor = (str = "") => {
  let h = 0;
  for (const c of str) h = (h * 31 + c.charCodeAt(0)) & 0xffffffff;
  const [a, b] = gradients[Math.abs(h) % gradients.length];
  return `linear-gradient(135deg, ${a}, ${b})`;
};

const initials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("") || "?";

const modeClass = (mode = "") => {
  const m = String(mode).toUpperCase();
  if (m.includes("FLASH")) return "modeBlue";
  if (m.includes("WRIT") || m.includes("SPELL")) return "modePurple";
  if (m.includes("LISTEN") || m.includes("AUDIO")) return "modeGreen";
  return "modeGray";
};

const timeAgo = (dateStr) => {
  try {
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 3600) return `${Math.max(1, Math.round(diff / 60))} min`;
    if (diff < 86400) return `${Math.round(diff / 3600)} h`;
    return `${Math.round(diff / 86400)} d`;
  } catch {
    return "";
  }
};

const GameRow = ({ it, t, showMode = true }) => {
  const name = it.user?.name || t("home.recentGames.anon", "Usuario");
  return (
    <div className={styles.row}>
      <div className={styles.avatar} style={{ background: gradFor(name) }}>
        {it.user?.avatarUrl ? (
          <img src={it.user.avatarUrl} alt="" />
        ) : (
          <span>{initials(name)}</span>
        )}
      </div>
      <div className={styles.info}>
        <div className={styles.userLine}>
          <span className={styles.name}>{name}</span>
          {it.date && <span className={styles.time}>{timeAgo(it.date)}</span>}
        </div>
        <div className={styles.metaLine}>
          {it.deckAvatarUrl ? (
            <img className={styles.deckThumb} src={it.deckAvatarUrl} alt="" />
          ) : (
            <span className={styles.deckDot} />
          )}
          <span className={styles.deckTitle}>{it.deckTitle || "—"}</span>
          {showMode && (
            <span className={cn(styles.tag, styles[modeClass(it.mode)])}>
              {it.mode || t("home.recentGames.game", "Partida")}
            </span>
          )}
          <span className={styles.meta}>
            {it.words ?? 0} {t("home.recentGames.words", "palabras")}
            {it.duration != null && ` · ${Math.max(1, Math.round(it.duration / 60))} min`}
          </span>
        </div>
      </div>
    </div>
  );
};

const RecentGames = ({ className }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const { value, isLoading, error } = useSelector((s) => s.stats.recentGames);
  const items = value?.results ?? [];

  useEffect(() => {
    dispatch(fetchRecentGames(CARD_ITEMS));
  }, [dispatch]);

  const openModal = () => {
    setModalOpen(true);
    if (items.length < MAX_TAKE) dispatch(fetchRecentGames(MAX_TAKE));
  };

  return (
    <>
      <Card
        className={cn(styles.card, className)}
        title={t("home.recentGames.title", "Últimas partidas")}
        classTitle="title-yellow"
      >
        <div className={styles.list}>
          {isLoading && items.length === 0 && (
            <div className={styles.state}>{t("home.recentGames.loading", "Cargando…")}</div>
          )}
          {error && (
            <div className={styles.state}>{t("home.recentGames.error", "No se pudo cargar")}</div>
          )}
          {!isLoading && !error && items.length === 0 && (
            <div className={styles.state}>{t("home.recentGames.empty", "Sin partidas recientes")}</div>
          )}
          {items.slice(0, CARD_ITEMS).map((it, i) => (
            <GameRow key={i} it={it} t={t} showMode={false} />
          ))}
        </div>

        {!error && items.length > 0 && (
          <button type="button" className={cn("button-stroke", styles.more)} onClick={openModal}>
            {t("home.recentGames.viewMore", "Ver más")}
          </button>
        )}
      </Card>

      <Modal
        visible={modalOpen}
        onClose={() => setModalOpen(false)}
        outerClassName={styles.modalOuter}
      >
        <div className={cn("title-yellow", styles.modalTitle)}>
          {t("home.recentGames.title", "Últimas partidas")}
        </div>
        <div className={styles.modalList}>
          {items.map((it, i) => (
            <GameRow key={i} it={it} t={t} />
          ))}
          {isLoading && (
            <div className={styles.loadingMore}>
              <span className={styles.spinner} />
              {t("home.recentGames.loadingMore", "Cargando más…")}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default RecentGames;
